/**
 * Error handling helper functions for consistent error processing across the codebase.
 */
import { BaseError } from './BaseError.js';
import { serializeStandardError } from './SerializedStandardError.js';
/**
 * Logs an error in a browser context using console.log.
 * Does not throw the error.
 * Useful for error logging in React components or browser environments.
 * @param error - The error to log.
 */
export function logBrowserError(error) {
    if (error instanceof Error) {
        // biome-ignore lint/suspicious/noConsole: Console logging is the intended behavior for browser error logging
        console.log('Error:', error.message, error);
    }
    else {
        // biome-ignore lint/suspicious/noConsole: Console logging is the intended behavior for browser error logging
        console.log('Error:', String(error));
    }
}
/**
 * Logs an error using abstract logger.  Will serialize information from BaseError classes.
 * @param error
 * @param message
 * @param logger
 */
export function logServerError(logger, error, message, context) {
    if (error instanceof BaseError) {
        logger.error(message, {
            ...context,
            baseError: serializeStandardError(error),
        });
    }
    else if (error instanceof Error) {
        logger.error(message, {
            ...context,
            error,
        });
    }
    else {
        logger.error(message, {
            ...context,
            unknownError: error,
        });
    }
}
/**
 * Re-throws any error inheriting from BaseError.
 * Otherwise converts the error to an Error and returns it for additional processing.
 * Meant to be used in catch blocks where you want to preserve standard errors
 * but convert other values to Error instances.
 * @param error - The error to handle.
 * @returns An Error object for additional processing.
 * @throws BaseError if the error inherits from BaseError.
 */
export function throwIfStandardError(error) {
    if (error instanceof BaseError) {
        throw error;
    }
    if (error instanceof Error) {
        return error;
    }
    return new Error(String(error));
}
/**
 * @deprecated Use throwIfStandardError, convertToStandardErrorAndThrow, or logBrowserError instead.
 * This is a backward-compatible alias for throwIfStandardError.
 *
 * Meant to be used in catch blocks.
 * Will re-raise any error inheriting from BaseError.
 * Otherwise will convert the error to an Error and return it for additional processing.
 * @param error - The error to handle.
 * @returns An Error object for additional processing.
 */
export function handleStandardError(error) {
    return throwIfStandardError(error);
}
/**
 * Converts any error to a BaseError and throws it.
 * If the error already extends BaseError, re-throws it immediately.
 * Otherwise converts to Error if needed and calls the conversion function.
 * Meant to be used in catch blocks where you want to wrap errors consistently.
 *
 * @param error - The error to handle.
 * @param convertFn - Function to convert an Error to a BaseError.
 * @throws The BaseError (either original or converted).
 *
 * @example
 * ```typescript
 * try {
 *   await externalOperation();
 * } catch (error) {
 *   convertToStandardErrorAndThrow(error, (err) =>
 *     new RuntimeError('Operation failed', { context: 'details' }, err)
 *   );
 * }
 * ```
 */
export function convertToStandardErrorAndThrow(error, convertFn) {
    if (error instanceof BaseError) {
        throw error;
    }
    const typedError = error instanceof Error ? error : new Error(String(error));
    throw convertFn(typedError);
}
/**
 * Returns an error handler function that logs errors and returns null.
 * Useful for handling promise rejections where you want to log errors but continue with a fallback value.
 *
 * @param logger - The logger instance to use for error logging.
 * @param errorMessage - The error message to log if the promise is rejected.
 * @param context - Optional context object to include in error logs.
 * @returns A function that can be passed to `.catch()` to handle errors.
 *
 * @example
 * ```typescript
 * // With individual promises
 * const job = await fetchJob().catch(handleErrorAndReturnNull(logger, 'Failed to fetch job', { jobId }));
 * const account = await fetchAccount().catch(handleErrorAndReturnNull(logger, 'Failed to fetch account', { accountId }));
 *
 * // With Promise.all
 * const [job, account] = await Promise.all([
 *   fetchJob().catch(handleErrorAndReturnNull(logger, 'Failed to fetch job', { jobId })),
 *   fetchAccount().catch(handleErrorAndReturnNull(logger, 'Failed to fetch account', { accountId })),
 * ]);
 * ```
 */
export function handleErrorAndReturnNull(logger, errorMessage, context) {
    return (error) => {
        logServerError(logger, error, errorMessage, context);
        return null;
    };
}
//# sourceMappingURL=ErrorHandlers.js.map